import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownLeft, ArrowUpRight, Copy, Gift, History, Plus, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { getWebApp, haptic } from '@/lib/telegram';
import { telegramShareUrl } from '@/lib/clipboard';
import { formatMinor, formatWholeMinor } from '@/lib/currency';
import { shortDateTime } from '@/lib/date';
import { intentLabel } from '@/lib/providers';
import { cn } from '@/lib/utils';
import { Btn, CountUp, Group, Row, ScreenTitle, SectionTitle } from '@/components/ui/tiles';
import { Skeleton, SkeletonLine, SkeletonPage } from '@/components/ui/skeleton';
import { ReferralWithdrawBlock } from './subscription';

/**
 * Вкладка «Кошелёк» (раскладка «Плитки», 19.10.2026): баланс и что с ним сделать,
 * приглашения друзей с заработком и выводом, последние операции. Раньше баланс жил в
 * профиле, приглашения внизу главной, история в отдельном разделе без входа с главной.
 */
export const Route = createFileRoute('/wallet')({ component: WalletScreen });

function WalletScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const { data: profile, isPending, isError: profileFailed, refetch: refetchProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.profile(),
    placeholderData: keepPreviousData,
  });
  const { data: history, isPending: historyPending, isError: historyFailed, refetch: refetchHistory } = useQuery({
    queryKey: ['payments-history'],
    queryFn: () => api.paymentsHistory(),
  });

  if (isPending && !profile) return <WalletSkeleton />;
  // Упавший профиль не равен «на балансе 0 ₽».
  if (profileFailed && !profile) {
    return (
      <div className="space-y-4 p-4">
        <ScreenTitle>{t('Wallet')}</ScreenTitle>
        <Group>
          <Row title={t('Wallet load failed')} hint={t('Check connection and retry')} onClick={() => void refetchProfile()} />
        </Group>
      </div>
    );
  }

  const currency = profile?.display_currency ?? 'RUB';
  const referralLink = profile?.referral_link ?? '';
  const shareReferral = () => {
    if (!referralLink) return;
    haptic('light');
    getWebApp()?.openTelegramLink(telegramShareUrl(referralLink, t('Referral share text')));
  };
  const copyReferral = async () => {
    if (!referralLink) {
      toast.error(t('Referral link missing'));
      return;
    }
    try {
      await navigator.clipboard.writeText(referralLink);
      haptic('success');
      toast.success(t('Link copied'));
    } catch {
      getWebApp()?.openTelegramLink(telegramShareUrl(referralLink, t('Referral share text')));
    }
  };

  const items = (history?.items ?? []).slice(0, 4);
  const balance = profile?.balance_display_minor ?? 0;

  return (
    <div className="space-y-5 p-4 pb-6">
      <ScreenTitle>{t('Wallet')}</ScreenTitle>

      {/* Баланс и кнопки одним блоком: в «Облаке» это одна подушка. */}
      <div className="tl-balance space-y-5">
      <section className="px-1">
        <div className="text-[14px] text-[var(--t-muted)]">{t('Balance')}</div>
        <div className="mt-1 text-[56px] font-semibold leading-[1.05] tracking-[-0.045em] tabular">
          <CountUp id="wallet-balance" value={balance} format={(n) => formatWholeMinor(Math.round(n), currency, i18n.language)} />
        </div>
      </section>
      <div className="grid grid-cols-2 gap-2.5">
        <Btn variant="strong" icon={<Plus />} onClick={() => navigate({ to: '/topup' })}>
          {t('Top up')}
        </Btn>
        <Btn variant="ghost" icon={<Gift />} onClick={() => navigate({ to: '/gift' })}>
          {t('Gift')}
        </Btn>
      </div>
      </div>

      {profile?.referral_enabled && (
        <section className="space-y-4 rounded-[var(--r-subcard)] border border-[var(--t-line)] bg-[var(--t-surface)] p-5 shadow-[var(--t-shadow)]">
          <div>
            <div className="flex items-baseline justify-between gap-3">
              <div className="text-[17px] font-semibold">{t('Invite friends')}</div>
              {/* Число приглашённых отдельно от блока вывода: тот скрыт, когда партнёр
                  вывод не включил, а это почти все. */}
              <div className="shrink-0 text-[13px] text-[var(--t-muted)]">
                <span className="text-[15px] font-semibold text-foreground tabular">{profile.referrals_invited ?? 0}</span>{' '}
                {t('Invited word', { count: profile.referrals_invited ?? 0 })}
              </div>
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-[46px] font-semibold leading-none tracking-[-0.04em] tabular">{profile.referral_l1_percent ?? 15}%</span>
              <span className="text-[15px] font-medium text-[var(--t-muted)]">{t('From each payment of friend')}</span>
            </div>
            {(profile.referral_l2_percent ?? 0) > 0 && (
              <p className="mt-1.5 text-[13px] text-[var(--t-muted)]">{t('Invite friends hint l2', { l2: profile.referral_l2_percent })}</p>
            )}
          </div>
          <ReferralWithdrawBlock />
          {/* Отправить другу основное действие: два тапа до чата, как в боте. */}
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Btn icon={<Send />} onClick={shareReferral} disabled={!referralLink}>
              {t('Send to friend')}
            </Btn>
            <Btn variant="ghost" className="w-[52px] px-0" icon={<Copy />} label={t('Copy Link')} onClick={copyReferral} disabled={!referralLink} />
          </div>
        </section>
      )}

      {/* Вывод не зависит от рефералки: партнёр мог выключить программу, а у клиента
          осталась заявка или заработок, который можно вывести. */}
      {!profile?.referral_enabled && <ReferralWithdrawBlock />}

      <section>
        <SectionTitle action={t('All operations')} onAction={() => navigate({ to: '/payments' })}>
          {t('History')}
        </SectionTitle>
        <Group>
          {historyPending && !history ? (
            [0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3.5 px-4 py-3.5">
                <Skeleton className="size-9 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <SkeletonLine className="w-1/2" />
                  <SkeletonLine size="xs" className="w-1/3" />
                </div>
                <SkeletonLine className="w-14" />
              </div>
            ))
          ) : historyFailed && !history ? (
            <Row title={t('Payments load failed')} hint={t('Check connection and retry')} onClick={() => void refetchHistory()} />
          ) : items.length === 0 ? (
            <Row icon={<History />} title={t('Payments empty')} />
          ) : (
            items.map((p) => {
              const incoming = p.signed > 0;
              // Неудачный счёт не зачёркиваем и не красим зелёным: сумма обычным цветом,
              // что с ней стало, словом под ней.
              const failed = p.source === 'payment' && !['paid', 'refunded'].includes(p.status);
              return (
                <Row
                  key={p.id}
                  icon={incoming ? <ArrowDownLeft /> : <ArrowUpRight />}
                  title={intentLabel(p.intent_type, p.signed, p.source)}
                  hint={shortDateTime(p.created_at, i18n.language)}
                  right={
                    <span className="shrink-0 text-right">
                      <span className={cn('block text-[15px] font-medium tabular', incoming && !failed ? 'text-[var(--t-ok)]' : 'text-foreground')}>
                        {incoming ? '+' : '−'}
                        {p.currency === 'XTR' ? `${p.amount_display_minor} ⭐` : formatMinor(p.amount_display_minor, p.currency, i18n.language)}
                      </span>
                      {failed && (
                        <span className={cn('mt-0.5 block text-[12px]', p.status === 'pending' ? 'text-[var(--t-warn)]' : 'text-[var(--t-faint)]')}>
                          {t(PAY_STATUS[p.status] ?? 'Payment status failed')}
                        </span>
                      )}
                    </span>
                  }
                />
              );
            })
          )}
        </Group>
      </section>
    </div>
  );
}

/** Подписи статуса счёта. Незнакомый статус показываем как «не прошёл». */
const PAY_STATUS: Record<string, string> = {
  pending: 'Payment status pending',
  failed: 'Payment status failed',
  cancelled: 'Payment status cancelled',
  expired: 'Payment status expired',
};

function WalletSkeleton() {
  const { t } = useTranslation();
  return (
    <SkeletonPage className="space-y-5 p-4">
      <ScreenTitle>{t('Wallet')}</ScreenTitle>
      <div className="space-y-2 px-1">
        <Skeleton className="h-4 w-16 rounded" />
        <Skeleton className="h-14 w-32 rounded-lg" />
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <Skeleton className="h-[52px] rounded-[var(--r-btn)]" />
        <Skeleton className="h-[52px] rounded-[var(--r-btn)]" />
      </div>
      <Skeleton className="h-[280px] w-full rounded-[var(--r-subcard)]" />
      <Skeleton className="h-[200px] w-full rounded-[var(--r-card)]" />
    </SkeletonPage>
  );
}
